import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

interface POState {
  purchaseOrders: any[];
  currentPO: any | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

const initialState: POState = {
  purchaseOrders: [],
  currentPO: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0,
  },
};

export const fetchPurchaseOrders = createAsyncThunk(
  'po/fetchAll',
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/purchase-orders', { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch POs');
    }
  }
);

export const fetchPOById = createAsyncThunk(
  'po/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/purchase-orders/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch PO');
    }
  }
);

export const createPO = createAsyncThunk(
  'po/create',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/purchase-orders', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create PO');
    }
  }
);

export const acceptPO = createAsyncThunk(
  'po/accept',
  async ({ id, notes }: { id: string; notes?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/purchase-orders/${id}/accept`, { notes });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to accept PO');
    }
  }
);

export const rejectPO = createAsyncThunk(
  'po/reject',
  async ({ id, reason }: { id: string; reason: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/purchase-orders/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject PO');
    }
  }
);

export const exportPOToPDF = createAsyncThunk(
  'po/exportPDF',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/purchase-orders/${id}/pdf`, {
        responseType: 'blob',
      });

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export PDF');
    }
  }
);

export const addPOComment = createAsyncThunk(
  'po/addComment',
  async ({ id, text }: { id: string; text: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/purchase-orders/${id}/comments`, { text });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add comment');
    }
  }
);

const poSlice = createSlice({
  name: 'purchaseOrders',
  initialState,
  reducers: {
    clearCurrentPO: (state) => {
      state.currentPO = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseOrders = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPOById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPOById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPO = action.payload;
      })
      .addCase(createPO.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPO.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseOrders.unshift(action.payload);
      })
      .addCase(createPO.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(acceptPO.fulfilled, (state, action) => {
        const index = state.purchaseOrders.findIndex(po => po._id === action.payload._id);
        if (index !== -1) {
          state.purchaseOrders[index] = action.payload;
        }
        if (state.currentPO?._id === action.payload._id) {
          state.currentPO = action.payload;
        }
      })
      .addCase(rejectPO.fulfilled, (state, action) => {
        const index = state.purchaseOrders.findIndex(po => po._id === action.payload._id);
        if (index !== -1) {
          state.purchaseOrders[index] = action.payload;
        }
      })
      .addCase(exportPOToPDF.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportPOToPDF.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportPOToPDF.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addPOComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPOComment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPO = action.payload;
      })
      .addCase(addPOComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentPO, clearError } = poSlice.actions;
export default poSlice.reducer;